import React, {useState} from "react";
import {WORKSITE_REFERENCES_APPROVERS} from "../utils/constants";

import Button from "../ui-components/Button";
import SearchSelect from "../ui-components/SearchSelect";
import {CheckCircleIcon, DocumentTextIcon, PaperAirplaneIcon, ShareIcon} from "@heroicons/react/24/solid";
import {
  ClockIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CheckIcon,
  ArrowPathIcon,
  CalendarDaysIcon
} from "@heroicons/react/24/outline";
import WorksiteShareFromReferenceModal from "./Modals/WorksiteShareFromReferenceModal";
import {PageSection} from "../ui-components/Container";
import AuthenticationService from "../services/AuthenticationService";
import {getPDF} from "../utils";


const ReferenceApproval = ({
    intl,
    push,
    approvals,
    setApprovals,
    linkedUsers,
    setLinkedUsers,
    worksite_id,
    worksite_name,
    reference,
    countryApiRequest,
    editable,
}) => {

  const userData = AuthenticationService.getUserData();
  const userScopes = AuthenticationService.getUserScopes();

  const [approvalsEdits, setApprovalsEdits] = useState();
  const [approvalsEditMode, setApprovalsEditMode] = useState(false);
  const [shareWorksiteOpen, setShareWorksiteOpen] = useState(false);
  const [approvalsEditsLoading, setApprovalsEditsLoading] = useState(false);
  const [sendingApprovalRequestType, setSendingApprovalRequestType] = useState();
  const [downloadingLiberatoria, setDownloadingLiberatoria] = useState(false);

  const generateApproval = (id, name, type) => ({
    cod_reference_id: reference.id,
    des_approval_type: type,
    cod_approved_by: id,
    des_approved_by: name,
    des_approved_by_email: linkedUsers?.find((u) => u.id === id)?.email
  });

  const updateApprovalsEdits = (wra, items) => {
    const itemsList = wra.multiple ? items : [items]
    setApprovalsEdits(
      approvalsEdits
        // remove approvals of the current type that aren't in the provided items
        .filter((a) => a.des_approval_type !== wra.name || itemsList.some((i) => i.value === a.cod_approved_by))
        // add new approvals of the current type that aren't already in the main array
        .concat(itemsList
          .filter((i) => !approvalsEdits.some((a) => a.des_approval_type === wra.name && i.value === a.cod_approved_by))
          .map((i) => generateApproval(i.value, i.label, wra.name))
        )
    )
  }

  const updateApprovals = async (edits) => {

    const toInsert = edits.filter((e) => !('id' in e));
    const toDelete = approvals
        ?.filter((a) => !edits.some((e) =>
          a.des_approval_type === e.des_approval_type &&
          a.cod_approved_by === e.cod_approved_by
        ))
      ??
      [];
    const toDeleteIds = toDelete.map((a) => a.id);

    setApprovalsEditsLoading(true);
    let inserted = [];
    try {
      // 1. insert new approvers on db (if any)
      if (toInsert.length > 0)
        inserted = await Promise.all(toInsert.map((ea) =>
          countryApiRequest.post(
            '/worksites_references_approvals',
            {...ea, r_perm: ['public'], w_perm: ['public']}
          )
        ))
      if (toDelete.length > 0) {
        // 2. delete deprecated approvers from db (if any)
        await Promise.all(toDeleteIds.map((aid) =>
          countryApiRequest.delete(`/worksites_references_approvals/${aid}`)
        ))
        // 3. ensure that the reference is not visible anymore to the deleted approvers
        const toDeleteUserIds = toDelete.map((a) => `user_${a.cod_approved_by}`);
        await countryApiRequest.put(
          `/worksites_references/${reference.id}`,
          {
            ...reference,
            r_perm: reference.r_perm.filter((rp) => !toDeleteUserIds.includes(rp))
          }
        );
      }
    } catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
      return false;
    } finally {
      setApprovalsEditsLoading(false);
    }

    setApprovals([
      ...(approvals?.filter((oa) => !inserted.some((ea) => ea.id === oa.id) && !toDeleteIds.includes(oa.id)) ?? []),
      ...inserted
    ])
    setApprovalsEdits(null)
    push({type: 'success', title: intl.formatMessage({id: 'approvals_updated_successfully'})})

    return true;
  }

  const sendApprovalRequest = async (approvalType) => {
    setSendingApprovalRequestType(approvalType);
    try {
      const updates = await countryApiRequest.post(
        '/worksites_references_approvals/send_approval_requests',
        {
          worksite_id: reference.cod_worksite_id,
          worksite_name: worksite_name,
          reference_id: reference.id,
          reference_title: reference.content?.title ?? intl.formatMessage({id: 'untitled'}),
          approval_type: approvalType
        }
      )
      setApprovals(approvals.map((a) => {
        const u = updates.find((u) =>
          a.des_approval_type === approvalType &&
          u.des_approval_type === a.des_approval_type &&
          u.cod_approved_by === a.cod_approved_by
        );
        return u ?? a;
      }));
      push({type: 'success', title: intl.formatMessage({id: 'approval_request_sent'})})
    }
    catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
      throw error;
    }
    finally {
      setSendingApprovalRequestType(null);
    }
  }

  const userCanSeeApprovalSendButton =
    userScopes.includes('reference:update') &&
    (userData.role_type === 'admin' || userData.email === reference.creator_email);


  return (
    <PageSection
      title={intl.formatMessage({id: 'approval_requests'})}
      editMode={editable ? approvalsEditMode : null}
      onEdit={() => {
        setApprovalsEditMode(true);
        setApprovalsEdits(approvals?.length > 0 ? approvals : []);
      }}
      onConfirm={async () => {
        const success = await updateApprovals(approvalsEdits);
        if (success)
          setApprovalsEditMode(false);
      }}
      onCancel={() => {
        setApprovalsEditMode(false);
      }}
      loading={approvalsEditsLoading}
      intl={intl}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {
          WORKSITE_REFERENCES_APPROVERS.map((wra, idx) => {
            const currentApprovals = !approvalsEditMode ? approvals : approvalsEdits;
            const currentTypeApprovals = currentApprovals?.filter((a) => a.des_approval_type === wra.name) ?? [];
            const currentTypeFullyApproved = currentTypeApprovals.length > 0 && currentTypeApprovals.every((a) => a.tms_approved_on);
            const approvalRequestSent = currentTypeApprovals.every((cta) => cta.tms_sent_request_on);
            const approvalSendingDate = currentTypeApprovals[0]?.tms_sent_request_on?.split('T')[0];
            const currentTypeDependencies = currentApprovals
              ?.filter((a) => wra.dependencies.includes(a.des_approval_type));
            const allDependenciesFulfilled = [
              // check 1: there must be at least one
              wra.dependencies.every((dep) => currentTypeDependencies?.some((ctd) => ctd.des_approval_type === dep) ?? false),
              // check 2: all dependencies must be fulfilled
              currentTypeDependencies?.every((a) => a.tms_approved_on)
            ].every((cond) => cond === true)

            return <div key={idx}>
              {
                approvalsEditMode
                  ? <>
                    <h3 className="flex gap-2 mb-1">
                      <span>{intl.formatMessage({id: wra.name})}</span>
                      {currentTypeFullyApproved && <CheckCircleIcon className="w-5 h-5 text-kerakoll-700"/>}
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-3 w-full justify-between">
                      <div className="w-full">
                        <SearchSelect
                          value={currentTypeApprovals.map((cta) => ({
                            label: cta.des_approved_by,
                            value: cta.cod_approved_by
                          }))}
                          onChange={(selected) => {
                            updateApprovalsEdits(wra, selected);
                          }}
                          options={
                            linkedUsers
                              ?.filter((u) => wra.roles.includes(u.role?.name))
                              ?.map((u) => ({
                                label: `${u.firstname} ${u.lastname}`,
                                value: u.id
                              }))
                            ?? []
                          }
                          isMulti={wra.multiple}
                        />
                      </div>
                    </div>
                    {/*{*/}
                    {/*  currentTypeApprovals.length === 0 && <p className="text-xs text-red-600">*/}
                    {/*    {intl.formatMessage({id: 'required_field'})}*/}
                    {/*  </p>*/}
                    {/*}*/}
                  </>
                  : <>
                    <div className="mb-2 font-semibold">
                      {intl.formatMessage({id: wra.name})}
                    </div>
                    <div className="flex flex-col gap-1">
                      {
                        currentTypeApprovals.length > 0
                          ? <>
                            {
                              currentTypeApprovals.map((cta, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    {
                                      cta.tms_approved_on
                                        ? <CheckCircleIcon className="w-4 h-4 text-kerakoll-700"/>
                                        : <ClockIcon className="w-4 h-4 text-gray-400"/>
                                    }
                                    <span>{cta.des_approved_by}</span>
                                    {
                                      cta.tms_approved_on && wra.signature && (
                                        <DocumentTextIcon
                                          className={`w-4 h-4 ${downloadingLiberatoria ? 'text-gray-200' : 'cursor-pointer'}`}
                                          onClick={() => {
                                            if (downloadingLiberatoria)
                                              return;
                                            setDownloadingLiberatoria(true)
                                            getPDF(
                                              intl,
                                              push,
                                              countryApiRequest,
                                              '/reference/approval',
                                              {
                                                id_reference: cta.cod_reference_id,
                                                id_user_approval: cta.cod_approved_by,
                                              },
                                              `liberatoria_progettista_${worksite_id}-${reference.id}.pdf`,
                                              () => {},
                                              () => {},
                                              true
                                            )
                                              .finally(() => {
                                                setDownloadingLiberatoria(false)
                                              })
                                          }}
                                        />
                                      )
                                    }
                                  </div>
                              ))
                            }
                            {
                              userCanSeeApprovalSendButton && !currentTypeFullyApproved && <>
                                <Button
                                  full
                                  styleType={approvalRequestSent ? 'blueWhite' : 'blue'}
                                  className="mt-2"
                                  paddingCls="px-4 py-1.5"
                                  disabled={
                                    approvalRequestSent ||  // approval request has already been sent, and we are waiting for it to be fulfilled
                                    !allDependenciesFulfilled ||  // approval request cannot be sent due to unfulfilled dependencies
                                    sendingApprovalRequestType === wra.name ||  // approval is being sent
                                    !reference?.content?.title ||  // reference title hasn't been defined yet
                                    !reference?.content?.description  // reference description hasn't been defined yet
                                  }
                                  onClick={async () => { await sendApprovalRequest(wra.name); }}
                                >
                                  {
                                    approvalRequestSent
                                      ? <CheckIcon className="w-4 h-4" />
                                      : (
                                        sendingApprovalRequestType === wra.name
                                          ? <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                          : <PaperAirplaneIcon className="w-3 h-3" />
                                      )
                                  }
                                  <span className="mx-1 whitespace-nowrap">
                                    {intl.formatMessage({id: approvalRequestSent ? 'request_sent' : 'send_request'})}
                                  </span>
                                </Button>
                                {
                                  approvalRequestSent && approvalSendingDate && (
                                    <div className="flex items-center gap-1 text-xs text-gray-400">
                                      <CalendarDaysIcon className="w-4" />
                                      <span className="whitespace-nowrap">
                                        {intl.formatMessage({id: 'sending_date'})}: {approvalSendingDate}
                                      </span>
                                    </div>
                                  )
                                }
                              </>
                            }
                          </>
                          : <div className="flex items-center gap-2">
                            <QuestionMarkCircleIcon className="w-4 h-4 text-gray-400"/>
                            <span>{intl.formatMessage({id: 'undefined'})}</span>
                          </div>
                      }
                    </div>
                  </>
              }
            </div>
          })
        }
      </div>
      {
        approvalsEditMode && <div className="flex  items-center gap-3 text-gray-400">
          <InformationCircleIcon className="w-4 h-4 min-w-max" />
          <div className="text-xs">
            <p className="font-semibold">{intl.formatMessage({id: 'reference_approver_not_visible_title'})}</p>
            <p>{intl.formatMessage({id: 'reference_approver_not_visible_dialog'})}</p>
          </div>
          <Button onClick={() => { setShareWorksiteOpen(true) }}>
            <ShareIcon className="w-4 h-4" />
            <span className="mx-1">{intl.formatMessage({id: 'share'})}</span>
          </Button>
        </div>
      }
      {
        shareWorksiteOpen && (
          <WorksiteShareFromReferenceModal
            closeModal={() => setShareWorksiteOpen(false)}
            worksite_id={worksite_id}
            setLinkedUsers={setLinkedUsers}
          />
        )
      }
    </PageSection>
  )
}

export default ReferenceApproval;